import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Loader2, Check, CheckCircle } from 'lucide-react'
import type { SeasonBracket, RegistrationWithProfile } from './types'

interface AssignBracketDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  registration: RegistrationWithProfile | null
  brackets: SeasonBracket[]
  selectedBracketId: string
  onBracketSelect: (bracketId: string) => void
  onConfirm: () => Promise<void>
  processing: boolean
}

export function AssignBracketDialog({
  open,
  onOpenChange,
  registration,
  brackets,
  selectedBracketId,
  onBracketSelect,
  onConfirm,
  processing,
}: AssignBracketDialogProps) {
  if (!registration) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Confirm Registration</DialogTitle>
          <DialogDescription>
            Assign {registration.profile.displayName} to a bracket
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm font-medium">Player's Preference</Label>
            <p className="text-sm text-muted-foreground">
              {registration.preferredBracketName || 'No preference specified'}
            </p>
          </div>
          <div className="space-y-2">
            <Label>Assign to Bracket</Label>
            <div className="space-y-2">
              {brackets.map(bracket => (
                <button
                  key={bracket.bracketId}
                  onClick={() => onBracketSelect(bracket.bracketId)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    selectedBracketId === bracket.bracketId
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <span className="font-medium">{bracket.name}</span>
                  {selectedBracketId === bracket.bracketId && (
                    <CheckCircle className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              disabled={processing || !selectedBracketId}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirm
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
